import React, { useMemo } from 'react';

import cn from 'classnames';

import { Icon } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import ProfilePics from 'components/candidate/ProfilePics';
import { SponsorShipPlanType } from 'lib/models/sponsorship-plans';
import { durationType } from 'utils/format';
import { getCurrencySymbol } from 'utils/common.utils';
import styles from './PlanCard.module.scss';

interface PlanCardProps {
  isEmpty?: boolean;
  plan?: SponsorShipPlanType;
  onCreatePlan?: () => void;
  onDeletePlan?: () => void;
  onEditPlan?: () => void;
}

export const PlanCard: React.FC<PlanCardProps> = (props: PlanCardProps) => {
  const { isEmpty, plan, onCreatePlan, onDeletePlan, onEditPlan } = props;

  const renderEmptyContent = useMemo(
    () => (
      <div>
        <div className={styles.info}>
          <Icon iconName="icon_info" className={styles.infoIcon} />
          <p>
            Create up to 3 pricing plans for attracting companies to sponsor
            your society.
          </p>
        </div>
        <ButtonV3
          isFullWidth
          color="secondary"
          size="large"
          onClick={onCreatePlan}
        >
          Create a plan
        </ButtonV3>
      </div>
    ),
    []
  );

  const renderContent = useMemo(() => {
    const {
      title,
      description,
      cost,
      currency,
      duration_type,
      huzzle_features,
      custom_features,
    } = plan || {};
    const allFeatures = [
      ...(huzzle_features || []),
      ...(custom_features || []),
    ];

    return (
      <>
        <div className={styles.PlanCardDetails}>
          <p className={styles.planName}>{title}</p>
          <p className={styles.planDesc}>{description}</p>
        </div>
        <div className={styles.PlanCardPricing}>
          <p className={styles.amount}>
            {getCurrencySymbol(currency!)}
            {cost || 0}
          </p>
          {duration_type && (
            <p className={styles.frequency}>{durationType(duration_type)}</p>
          )}
        </div>
        <div className={styles.PlanCardFeatures}>
          <p className={styles.featureTitle}>Features</p>
          <div className={styles.featureList}>
            {allFeatures.slice(0, 7).map((feature: string, index: number) => (
              <p key={index}>{feature}</p>
            ))}
            {allFeatures.length > 7 && <p>...</p>}
          </div>
        </div>
        <div className={styles.PlanCardCompanies}>
          {plan && plan?.companies?.length ? (
            <div>
              <ProfilePics
                maxPicsToShow={
                  plan.companies.length > 2 ? 2 : plan.companies.length
                }
                urls={plan.companies.map((company) => company.icon_url)}
                displayCount
              />
              <p>
                {plan.companies.length}{' '}
                {plan.companies.length > 1 ? 'companies' : 'company'} on this
                plan
              </p>
            </div>
          ) : null}
        </div>
        <div className={styles.PlanCardAction}>
          <ButtonV3
            disabled={!plan?.editable}
            color="secondary"
            onClick={onEditPlan}
          >
            Edit
          </ButtonV3>
          <ButtonV3
            disabled={!plan?.editable}
            color="secondary"
            onClick={onDeletePlan}
          >
            Delete
          </ButtonV3>
        </div>
      </>
    );
  }, [plan, onEditPlan]);

  return (
    <div className={cn(styles.PlanCard, { [styles.PlanCardEmpty]: isEmpty })}>
      {isEmpty ? renderEmptyContent : renderContent}
    </div>
  );
};
